/**
 * Single source of truth for password rules.
 *
 * These MUST stay in lockstep with the Supabase Auth server policy
 * (`supabase/config.toml` -> minimum_password_length / password_requirements,
 * and the same two fields in the dashboard under Authentication -> Sign In /
 * Providers -> Email). If the app is laxer than the server, users get a bare
 * "Password is too weak" 422 from Auth with no explanation of which rule they
 * missed. If the app is stricter, users are blocked from passwords Auth would
 * happily accept.
 *
 * Policy (2026-08-04): minimum 10 characters, NO character-composition rules.
 *
 * Why no complexity requirements: NIST SP 800-63B advises against them --
 * forcing upper/lower/digit/symbol pushes people toward predictable patterns
 * ("Password1!") that are weaker than a longer, simpler passphrase. OWASP
 * agrees: favour length over composition. 10 rather than NIST's floor of 8
 * because the control that actually catches bad passwords -- checking them
 * against a breached-password corpus -- needs a Supabase Pro plan and is
 * currently off. The extra two characters are the compensation for that gap.
 * If leaked-password protection is ever enabled, 8 would be defensible again.
 */

/** Keep in sync with supabase/config.toml `minimum_password_length`. */
export const PASSWORD_MIN_LENGTH = 10;

const passwordLength = (password: string) => Array.from(password ?? '').length;

export interface PasswordRule {
  /** Stable key for test selectors and React list keys. */
  id: string;
  /** Shown to the user, phrased as the requirement (not the failure). */
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => passwordLength(p) >= PASSWORD_MIN_LENGTH,
  },
];

export interface PasswordCheck {
  valid: boolean;
  /** Every rule with its pass/fail state — drives the live checklist. */
  results: Array<PasswordRule & { passed: boolean }>;
  /** Only the rules not yet met. */
  unmet: PasswordRule[];
}

export function checkPassword(password: string): PasswordCheck {
  const value = password ?? '';
  const results = PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(value) }));
  const unmet = results.filter((r) => !r.passed);
  return { valid: unmet.length === 0, results, unmet };
}

/**
 * Human-readable explanation of what's still missing, for a toast or inline
 * error after a failed submit. Returns null when the password is valid.
 *
 * Says how many more characters are needed rather than just restating the
 * rule — "needs 3 more characters" is actionable, "must be at least 10
 * characters" makes the user count.
 */
export function describePasswordFailure(password: string): string | null {
  const value = password ?? '';
  const { valid } = checkPassword(value);
  if (valid) return null;

  const length = passwordLength(value);
  const short = PASSWORD_MIN_LENGTH - length;
  if (length === 0) {
    return `Please enter a password of at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  return `Your password needs ${short} more character${short === 1 ? '' : 's'} (${PASSWORD_MIN_LENGTH} minimum).`;
}

/**
 * Boolean form kept for drop-in compatibility with existing callers
 * (ResetPassword.tsx, admin/AdminLogin.tsx).
 */
export function validatePassword(password: string): boolean {
  return checkPassword(password).valid;
}

/** Maps Auth's unstable weak/leaked-password errors onto actionable copy. */
export function describePasswordServerError(error: unknown, password: string): string {
  const message = error instanceof Error
    ? error.message
    : String((error as { message?: string } | null)?.message ?? 'Password update failed.');
  const localFailure = describePasswordFailure(password);
  if (localFailure) return localFailure;
  if (/leak|breach|pwn|common password/i.test(message)) {
    return 'That password appears in a known data breach. Please choose a different password.';
  }
  if (/weak|password.*(short|length|character)/i.test(message)) {
    return `Please choose a password of at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  return message;
}
